/**
 * System Monitor Tab - Phase 1 (Read-Only)
 * 
 * Displays system status by reading from existing data:
 * - Model training status
 * - Signal generation stats (last 24h)
 * - Recent errors from logs (last 24h)
 * - MTF snapshot status
 * - System configuration
 * 
 * Admin-only access with manual refresh.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Clock,
  Activity,
  Database,
  Settings,
  AlertOctagon,
  TrendingUp,
  Cpu
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemMonitorTab = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch system status
  const fetchSystemStatus = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/system-status/full`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
      setLastRefresh(new Date());
      
      if (showToast) {
        toast.success('System status refreshed');
      }
    } catch (err) {
      console.error('Error fetching system status:', err);
      setError(err.message);
      if (showToast) {
        toast.error(`Failed to refresh: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchSystemStatus(false);
  }, [fetchSystemStatus]);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchSystemStatus(true);
  };

  // Format date/time
  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  // Format age
  const formatAge = (hours) => {
    if (hours === null || hours === undefined) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-lg">Loading system status...</span>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="glass-card p-8 rounded-xl border border-red-500/30 text-center">
        <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
        <h3 className="text-xl font-semibold mb-2 text-red-400">Failed to Load System Status</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-xl transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const { model_status, signal_stats_24h, recent_errors, mtf_snapshot_status, system_config } = data || {};

  // Count model stats
  const totalModels = model_status?.length || 0;
  const healthyModels = model_status?.filter(m => m.lstm_exists && !m.is_stale).length || 0;
  const staleModels = model_status?.filter(m => m.is_stale && m.lstm_exists).length || 0;
  const missingModels = model_status?.filter(m => !m.lstm_exists).length || 0;

  // Count errors
  const errorCount = recent_errors?.filter(e => e.level === 'ERROR').length || 0;
  const warningCount = recent_errors?.filter(e => e.level === 'WARNING').length || 0;

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            System Monitor
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Last refreshed: {lastRefresh ? formatDateTime(lastRefresh.toISOString()) : 'Never'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
          data-testid="system-monitor-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Models Status Card */}
        <div className="glass-card p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Cpu className="w-5 h-5 text-blue-400" />
            <span className="font-semibold">ML Models</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total:</span>
              <span>{totalModels}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400">Healthy:</span>
              <span className="text-green-400">{healthyModels}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400">Stale (&gt;1d):</span>
              <span className="text-yellow-400">{staleModels}</span>
            </div>
            {missingModels > 0 && (
              <div className="flex justify-between">
                <span className="text-red-400">Missing:</span>
                <span className="text-red-400">{missingModels}</span>
              </div>
            )}
          </div>
        </div>

        {/* Signals 24h Card */}
        <div className="glass-card p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <span className="font-semibold">Signals (24h)</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total:</span>
              <span>{signal_stats_24h?.total_signals || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400">BUY:</span>
              <span className="text-green-400">{signal_stats_24h?.buy_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-400">SELL:</span>
              <span className="text-red-400">{signal_stats_24h?.sell_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Executed:</span>
              <span>{signal_stats_24h?.executed_count || 0}</span>
            </div>
          </div>
        </div>

        {/* MTF Status Card */}
        <div className="glass-card p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Database className="w-5 h-5 text-purple-400" />
            <span className="font-semibold">MTF Snapshots</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total:</span>
              <span>{mtf_snapshot_status?.total_snapshots || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last 24h:</span>
              <span>{mtf_snapshot_status?.snapshots_24h || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Latest:</span>
              <span className="text-xs">{mtf_snapshot_status?.latest_snapshot ? new Date(mtf_snapshot_status.latest_snapshot).toLocaleTimeString() : 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Errors Card */}
        <div className={`glass-card p-4 rounded-xl border ${errorCount > 0 ? 'border-red-500/30' : warningCount > 0 ? 'border-yellow-500/30' : 'border-white/10'}`}>
          <div className="flex items-center gap-3 mb-3">
            <AlertOctagon className={`w-5 h-5 ${errorCount > 0 ? 'text-red-400' : warningCount > 0 ? 'text-yellow-400' : 'text-green-400'}`} />
            <span className="font-semibold">Logs (24h)</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-red-400">Errors:</span>
              <span className={errorCount > 0 ? 'text-red-400 font-bold' : ''}>{errorCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400">Warnings:</span>
              <span className={warningCount > 0 ? 'text-yellow-400' : ''}>{warningCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total:</span>
              <span>{recent_errors?.length || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Model Status Table */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Cpu className="w-5 h-5 text-blue-400" />
          Model Training Status
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-2">Coin</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Last Trained</th>
                <th className="text-left p-2">Age</th>
                <th className="text-left p-2">LSTM</th>
                <th className="text-left p-2">GB</th>
              </tr>
            </thead>
            <tbody>
              {model_status?.map((model) => (
                <tr key={model.coin} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-2 font-medium">{model.coin}</td>
                  <td className="p-2">
                    {!model.lstm_exists ? (
                      <span className="flex items-center gap-1 text-red-400">
                        <XCircle className="w-4 h-4" /> Missing
                      </span>
                    ) : model.is_stale ? (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <AlertTriangle className="w-4 h-4" /> Stale
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-4 h-4" /> Healthy
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-muted-foreground">
                    {model.last_trained ? new Date(model.last_trained).toLocaleString() : 'Never'}
                  </td>
                  <td className="p-2">
                    <span className={model.is_stale ? 'text-yellow-400' : ''}>
                      {formatAge(model.age_hours)}
                    </span>
                  </td>
                  <td className="p-2">
                    {model.lstm_exists ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                  </td>
                  <td className="p-2">
                    {model.gb_exists ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                  </td>
                </tr>
              ))}
              {(!model_status || model_status.length === 0) && (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-muted-foreground">
                    No models found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Errors Table */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertOctagon className="w-5 h-5 text-red-400" />
          Recent Errors & Warnings (Last 24h)
        </h3>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-black/50 backdrop-blur">
              <tr className="border-b border-white/10">
                <th className="text-left p-2">Time</th>
                <th className="text-left p-2">Level</th>
                <th className="text-left p-2">Service</th>
                <th className="text-left p-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {recent_errors?.map((entry, idx) => (
                <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-2 text-muted-foreground whitespace-nowrap">
                    {entry.timestamp}
                  </td>
                  <td className="p-2">
                    {entry.level === 'ERROR' ? (
                      <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs font-medium">
                        ERROR
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs font-medium">
                        WARN
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-muted-foreground text-xs">
                    {entry.service?.split('.').pop() || entry.service}
                  </td>
                  <td className="p-2 text-xs max-w-md truncate" title={entry.message}>
                    {entry.message}
                  </td>
                </tr>
              ))}
              {(!recent_errors || recent_errors.length === 0) && (
                <tr>
                  <td colSpan="4" className="p-4 text-center text-green-400">
                    <CheckCircle className="w-5 h-5 inline mr-2" />
                    No errors or warnings in the last 24 hours
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Configuration */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-400" />
          Current Configuration
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Take Profit:</span>
            <span className="ml-2 font-medium">{system_config?.take_profit_pct || 'N/A'}%</span>
          </div>
          <div>
            <span className="text-muted-foreground">Stop Loss:</span>
            <span className="ml-2 font-medium">{system_config?.stop_loss_pct || 'N/A'}%</span>
          </div>
          <div>
            <span className="text-muted-foreground">Position Size:</span>
            <span className="ml-2 font-medium">${system_config?.position_size || 'N/A'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Max Hold:</span>
            <span className="ml-2 font-medium">{system_config?.max_hold_minutes || 'N/A'} mins</span>
          </div>
          <div className="col-span-2 md:col-span-4">
            <span className="text-muted-foreground">Enabled Coins:</span>
            <span className="ml-2 font-medium">
              {system_config?.enabled_coins?.length > 0 
                ? system_config.enabled_coins.join(', ') 
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitorTab;
